import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import './funnelchart.css';

const FunnelChartComponent = ({ data }) => {
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const svg = d3
      .select(chartRef.current)
      .attr('width', 800)
      .attr('height', 400)
      .attr('viewBox', '0 0 800 450')
      .attr('preserveAspectRatio', 'xMidYMid meet');

    svg.selectAll('.marking-line, .marking-label').remove();
    svg.selectAll('.funnel-segment').remove();

    const funnelWidth = 600;
    const funnelHeight = 320;
    const funnelX = 50;
    const funnelY = 40;

    const funnel = svg.append('g').attr('transform', `translate(${funnelX}, ${funnelY})`);

    const totalSegments = 5;
    const fallbackColor = "#d3d3d3";
    const filledSegments = data.length;
    const missingSegments = totalSegments - filledSegments;

    const displayData = [
      ...data.map((item) => ({
        label: item.label,
        value: item.value,
        color: item.color,
      })),
      ...Array(missingSegments).fill({ label: "No Data", value: 20, color: fallbackColor }),
    ];

    const layouts = displayData.map((item, index) => {
      const isLast = index === displayData.length - 1;
      const baseWidth = 500;
      const minWidth = 80;
      const stepDecrease = (baseWidth - minWidth) / (displayData.length - 1);

      return {
        width: isLast ? minWidth : baseWidth - index * stepDecrease,
        height: isLast ? 90 : 75 + index * 5,
        color: item.color,
        diagonal: !isLast,
      };
    });

    let cumulativeHeight = 0;
    const maxLabelOffset = 280;

    layouts.forEach((d, i) => {
      const points = d.diagonal
        ? [
          [funnelWidth / 2 - d.width / 2, cumulativeHeight],
          [funnelWidth / 2 + d.width / 2, cumulativeHeight],
          [funnelWidth / 2 + (layouts[i + 1]?.width / 2 || d.width / 2), cumulativeHeight + d.height],
          [funnelWidth / 2 - (layouts[i + 1]?.width / 2 || d.width / 2), cumulativeHeight + d.height],
        ]
        : [
          [funnelWidth / 2 - d.width / 2, cumulativeHeight],
          [funnelWidth / 2 + d.width / 2, cumulativeHeight],
          [funnelWidth / 2 + d.width / 2, cumulativeHeight + d.height],
          [funnelWidth / 2 - d.width / 2, cumulativeHeight + d.height],
        ];

      cumulativeHeight += d.height;

      const segment = funnel
        .append('polygon')
        .attr('class', 'funnel-segment')
        .attr('points', points.map((p) => p.join(',')).join(' '))
        .attr('fill', d.color);

      if (data.length > 0 && displayData[i].label !== "No Data") {
        segment
          .on('mouseover', function (event) {
            const [x, y] = d3.pointer(event, svg.node());
            const tooltip = d3.select(tooltipRef.current);

            const containerRect = chartRef.current.getBoundingClientRect();
            const tooltipWidth = tooltip.node().offsetWidth;
            const tooltipHeight = tooltip.node().offsetHeight;
            const segmentRect = this.getBoundingClientRect();

            const tooltipX = segmentRect.left - containerRect.left + (segmentRect.width - tooltipWidth) / 2;
            const tooltipY = segmentRect.top - containerRect.top - tooltipHeight - 10;

            tooltip
              .style('left', `${tooltipX}px`)
              .style('top', `${tooltipY}px`)
              .style('display', 'block')
              .html(
                `<span class="legend-color" style="background-color: ${d.color};">
                </span> ${displayData[i].label} : <strong>${displayData[i].value}%</strong>`
              );
          })
          .on('mouseout', function () {
            d3.select(tooltipRef.current).style('display', 'none');
          });

        funnel
          .append('line')
          .attr('class', 'marking-line')
          .attr('x1', funnelWidth / 2 + d.width / 2)
          .attr('y1', cumulativeHeight - d.height / 2)
          .attr('x2', funnelWidth / 2 + maxLabelOffset)
          .attr('y2', cumulativeHeight - d.height / 2)
          .attr('stroke', '#2D3E50')
          .attr('stroke-width', 2);

        funnel
          .append('text')
          .attr('class', 'marking-label')
          .attr('x', funnelWidth / 2 + maxLabelOffset + 10)
          .attr('y', cumulativeHeight - d.height / 2)
          .attr('dy', '6px')
          .attr('text-anchor', 'start')
          .attr('fill', '#2D3E50')
          .attr('font-size', '40px')
          .text(`${displayData[i].value}%`);
      }
    });
  }, [data]);

  const hasData = data.length > 0;

  return (
    <div className={`funnel-chart-container ${!hasData ? 'no-data' : ''}`}>
      <svg ref={chartRef}></svg>
      <div className="tooltip" ref={tooltipRef}></div>
      {hasData && (
        <div className="funnel-legend">
          {data.map((item) => (
            <div key={item.label}>
              <span className="legend-color" style={{ backgroundColor: item.color }}></span>
              <span className="legend-text">{item.label.charAt(0).toUpperCase() + item.label.slice(1).toLowerCase()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FunnelChartComponent;